import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core'
import { ShopfloorLayoutContext } from '../shopfloor-layout-context'
import { ShopfloorLayoutSpec } from '../spec/shopfloor-layout-spec'
import { ShopfloorLayoutSpecNode } from '../shopfloor-layout-spec-node'
import { ShopfloorLayoutSpecEdge } from '../shopfloor-layout-spec-edge'
import cytoscape, {
  Css,
  CytoscapeOptions,
  EdgeDataDefinition,
  ElementDefinition,
  LayoutOptions,
  NodeDataDefinition,
  StylesheetJson,
  StylesheetStyle,
} from 'cytoscape'
import dagre, { DagreLayoutOptions } from 'cytoscape-dagre'
import { combineLatest, Observable, ReplaySubject } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { filter, map } from 'rxjs/operators'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { DurationFormatUtil, ElderThemeService, ThemeSpec } from '@elderbyte/ngx-starter'

type CytoscapeData = [ElementDefinition[], ElementDefinition[], ThemeSpec]

@Component({
  selector: 'app-shopfloor-layout-graph',
  templateUrl: './shopfloor-layout-graph.component.html',
  styleUrls: ['./shopfloor-layout-graph.component.scss'],
})
export class ShopfloorLayoutGraphComponent implements AfterViewInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  @Output() nodeClicked = new EventEmitter<ShopfloorLayoutSpecNode>()
  @Output() edgeClicked = new EventEmitter<ShopfloorLayoutSpecEdge>()
  @ViewChild('cy') container: ElementRef

  private readonly nodeLabelPropertyName = 'label'
  private readonly edgeLabelPropertyName = 'transportCost'
  private readonly workCenterPropertyName = 'workCenter'
  private readonly context$ = new ReplaySubject<ShopfloorLayoutContext>(1)
  private readonly spec$ = new ReplaySubject<ShopfloorLayoutSpec>(1)
  private readonly theme$: Observable<ThemeSpec>
  private readonly destroyRef = inject(DestroyRef)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const themeService = inject(ElderThemeService)
    this.theme$ = themeService.activeTheme$
  }

  /***************************************************************************
   *                                                                         *
   * Life cycle                                                              *
   *                                                                         *
   **************************************************************************/

  ngAfterViewInit(): void {
    const container = this.container.nativeElement
    combineLatest([this.context$, this.spec$, this.theme$])
      .pipe(
        takeUntilDestroyed(this.destroyRef), // used outside injection constext
        map(([context, spec, theme]) => {
          const nodes = this.toGraphNodes(context.nodes)
          const edges = this.toGraphEdges(spec.edges)
          return <CytoscapeData>[nodes, edges, theme]
        }),
        filter(([nodes, edges, _]) => {
          const edgeSources = ShopfloorLayoutGraphComponent.getEdgeSources(edges)
          const sources = ShopfloorLayoutGraphComponent.getNodeIds(nodes)
          return [...edgeSources].every((source) => sources.has(source))
        }),
        map(([nodes, edges, theme]) => this.toCytoscapeOptions(nodes, edges, theme, container))
      )
      .subscribe((options) => {
        cytoscape.use(dagre)
        const cy = cytoscape(options)
        cy.on('tap', 'node', (event) => this.nodeTapHandler(event))
        cy.on('tap', 'edge', (event) => this.edgeTapHandler(event))
      })
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set context(context: ShopfloorLayoutContext | null) {
    if (context) {
      this.log.debug('Updating shopfloor layout context')
      this.context$.next(context)
    }
  }

  @Input()
  public set spec(spec: ShopfloorLayoutSpec | null) {
    if (spec) {
      this.log.debug('Updating shopfloor layout spec')
      this.spec$.next(spec)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private toGraphNodes(shopfloorLayoutSpecNodes: ShopfloorLayoutSpecNode[]): ElementDefinition[] {
    return shopfloorLayoutSpecNodes.map(
      (node) =>
        <ElementDefinition>{
          data: <NodeDataDefinition>{
            id: node.id,
            group: 'nodes',
            [this.nodeLabelPropertyName]: `${node.workCenter.number} ${node.workCenter.name}`, // custom data
            [this.workCenterPropertyName]: node.workCenter,
          },
        }
    )
  }

  private toGraphEdges(edges: ShopfloorLayoutSpecEdge[]): ElementDefinition[] {
    return edges.map(
      (edge) =>
        <ElementDefinition>{
          data: <EdgeDataDefinition>{
            id: edge.id,
            source: edge.source,
            target: edge.target,
            [this.edgeLabelPropertyName]: edge.transportCost, // custom data
          },
        }
    )
  }

  private toCytoscapeOptions(
    nodes: ElementDefinition[],
    edges: ElementDefinition[],
    theme: ThemeSpec,
    container: HTMLElement
  ): CytoscapeOptions {
    const layout: LayoutOptions & DagreLayoutOptions = {
      name: 'dagre',
      rankDir: 'LR',
      rankSep: 200, // separation between ranks (default 50)
      nodeSep: 40,
      nodeDimensionsIncludeLabels: true,
      fit: false,
    }
    const elements: ElementDefinition[] = [...nodes, ...edges]
    const darkGray = '#595959' // dark theme background (#414141), but 30% lighter
    const lightGray = '#afafaf' // light theme background (#fafafa), but 30% darker
    const style: StylesheetJson = [
      <StylesheetStyle>{
        selector: 'node',
        style: <Css.Node>{
          backgroundColor: theme.dark ? '#809EC5' : '#BB9F61', // accent
          color: theme.dark ? '#1D1D1B' : '#F2F2F2', // structural black and white
          label: (element) => element.data(this.nodeLabelPropertyName),
          width: 'label',
          shape: 'rectangle',
          'text-halign': 'center',
          'text-valign': 'center',
          'padding-top': '5px',
          'padding-bottom': '5px',
          'padding-left': '5px',
          'padding-right': '5px',
        },
      },
      <StylesheetStyle>{
        selector: 'edge',
        style: <Css.Edge>{
          width: 3,
          'line-color': theme.dark ? darkGray : lightGray,
          'target-arrow-color': theme.dark ? darkGray : lightGray,
          'target-arrow-shape': 'triangle',
          'curve-style': 'bezier',
          color: theme.dark ? '#F2F2F2' : '#1D1D1B',
          label: (edge) => this.formatEdgeLabel(edge.data(this.edgeLabelPropertyName)?.duration),
        },
      },
    ]
    return <CytoscapeOptions>{
      container,
      layout,
      elements,
      style,
      zoom: 1,
      maxZoom: 5,
      minZoom: 0.5,
      autoungrabify: true,
    }
  }

  private formatEdgeLabel(duration: string): string {
    return DurationFormatUtil.formatDuration(duration)
  }

  private nodeTapHandler(evt: cytoscape.EventObjectNode): void {
    const node = evt.target
    this.nodeClicked.emit(
      new ShopfloorLayoutSpecNode(
        node.id(),
        node.data(this.nodeLabelPropertyName),
        node.data(this.workCenterPropertyName)
      )
    )
  }

  private edgeTapHandler(event: cytoscape.EventObjectEdge): void {
    const edge = event.target
    this.edgeClicked.emit(
      new ShopfloorLayoutSpecEdge(
        edge.id(),
        edge.source().id(),
        edge.target().id(),
        edge.data(this.edgeLabelPropertyName)
      )
    )
  }

  private static getEdgeSources(edges: ElementDefinition[]): Set<string> {
    return new Set(
      edges.map((edge) => <EdgeDataDefinition>edge.data).map((edgeData) => edgeData.source)
    )
  }

  private static getNodeIds(nodes: ElementDefinition[]): Set<string> {
    return new Set(
      nodes.map((node) => <NodeDataDefinition>node.data).map((nodeData) => nodeData.id)
    )
  }
}
